import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';

export interface MaintenanceSchedule {
  startTime: string; // ISO string or specific time format
  endTime: string;   // ISO string or specific time format
  isActive: boolean;
  message?: string;
  // Day-based scheduling
  allowedDays?: number[]; // 0=Sunday, 1=Monday, 2=Tuesday, etc.
  excludedDays?: number[]; // Days to exclude from maintenance
  timeZone?: string; // Timezone for day calculations
}

@Injectable({
  providedIn: 'root'
})
export class MaintenanceService {
  private partialMaintenanceModeSubject = new BehaviorSubject<boolean>(false);
  private maintenanceScheduleSubject = new BehaviorSubject<MaintenanceSchedule | null>(null);
  
  public partialMaintenanceMode$ = this.partialMaintenanceModeSubject.asObservable();
  public maintenanceSchedule$ = this.maintenanceScheduleSubject.asObservable();
  
  private maintenanceCheckInterval: any;
  private isChecking = false;

  constructor() {
    this.startMaintenanceTimeCheck();
  }

  setPartialMaintenanceMode(isPartial: boolean): void {
    console.log(`MaintenanceService: Setting partial maintenance mode to ${isPartial}`);
    this.partialMaintenanceModeSubject.next(isPartial);
  }

  getPartialMaintenanceMode(): boolean {
    return this.partialMaintenanceModeSubject.value;
  }

  clearMaintenanceMode(): void {
    console.log('MaintenanceService: Clearing maintenance mode');
    this.partialMaintenanceModeSubject.next(false);
  }

  isMaintenanceModeActive(): boolean {
    return this.partialMaintenanceModeSubject.value;
  }

  /**
   * Set maintenance schedule with time-based activation
   */
  setMaintenanceSchedule(schedule: MaintenanceSchedule): void {
    console.log('MaintenanceService: Setting maintenance schedule:', schedule);
    this.maintenanceScheduleSubject.next(schedule);
    
    // Immediately check if maintenance should be active
    this.checkMaintenanceTime();
  }

  /**
   * Get current maintenance schedule
   */
  getMaintenanceSchedule(): MaintenanceSchedule | null {
    return this.maintenanceScheduleSubject.value;
  }

  /**
   * Start periodic checking of maintenance time
   */
  private startMaintenanceTimeCheck(): void {
    if (this.isChecking) return;
    
    this.isChecking = true;
    // Check every minute
    this.maintenanceCheckInterval = interval(60000).subscribe(() => {
      this.checkMaintenanceTime();
    });
    
    // Initial check
    this.checkMaintenanceTime();
  }

  /**
   * Check if current time falls within maintenance window
   */
  private checkMaintenanceTime(): void {
    const schedule = this.maintenanceScheduleSubject.value;
    if (!schedule) return;

    const now = new Date();
    const startTime = new Date(schedule.startTime);
    const endTime = new Date(schedule.endTime);
    
    // Check if current time is within the time window
    const isWithinTimeWindow = now >= startTime && now <= endTime;
    
    // Check if current day is allowed for maintenance
    const isDayAllowed = this.isDayAllowedForMaintenance(schedule, now);
    
    // Maintenance is active if both time and day conditions are met
    const isWithinMaintenanceWindow = isWithinTimeWindow && isDayAllowed;
    
    console.log(`MaintenanceService: Time check - Current: ${now.toISOString()}, Start: ${startTime.toISOString()}, End: ${endTime.toISOString()}, Within time window: ${isWithinTimeWindow}, Day allowed: ${isDayAllowed}, Within maintenance window: ${isWithinMaintenanceWindow}`);
    
    if (isWithinMaintenanceWindow && !this.partialMaintenanceModeSubject.value) {
      console.log('MaintenanceService: Activating maintenance mode based on schedule');
      this.partialMaintenanceModeSubject.next(true);
    } else if (!isWithinMaintenanceWindow && this.partialMaintenanceModeSubject.value) {
      console.log('MaintenanceService: Deactivating maintenance mode based on schedule');
      this.partialMaintenanceModeSubject.next(false);
    }
  }

  /**
   * Check if the current day is allowed for maintenance
   */
  private isDayAllowedForMaintenance(schedule: MaintenanceSchedule, currentDate: Date): boolean {
    const currentDay = currentDate.getDay(); // 0=Sunday, 1=Monday, etc.
    
    // If no day restrictions, allow all days
    if (!schedule.allowedDays && !schedule.excludedDays) {
      return true;
    }
    
    // Check if current day is explicitly excluded
    if (schedule.excludedDays && schedule.excludedDays.includes(currentDay)) {
      return false;
    }
    
    // Check if current day is in allowed days (if specified)
    if (schedule.allowedDays && schedule.allowedDays.length > 0) {
      return schedule.allowedDays.includes(currentDay);
    }
    
    // If only excludedDays is specified, allow all other days
    return true;
  }

  /**
   * Get time until maintenance starts
   */
  getTimeUntilMaintenance(): Observable<string | null> {
    return this.maintenanceSchedule$.pipe(
      map(schedule => {
        if (!schedule) return null;
        
        const now = new Date();
        const startTime = new Date(schedule.startTime);
        
        if (now >= startTime) return null; // Maintenance already started
        
        const diff = startTime.getTime() - now.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        return `${hours}h ${minutes}m`;
      })
    );
  }

  /**
   * Get time until maintenance ends
   */
  getTimeUntilMaintenanceEnds(): Observable<string | null> {
    return this.maintenanceSchedule$.pipe(
      map(schedule => {
        if (!schedule) return null;
        
        const now = new Date();
        const endTime = new Date(schedule.endTime);
        
        if (now >= endTime) return null; // Maintenance already ended
        
        const diff = endTime.getTime() - now.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        return `${hours}h ${minutes}m`;
      })
    );
  }

  /**
   * Get next maintenance day information
   */
  getNextMaintenanceDay(): Observable<string | null> {
    return this.maintenanceSchedule$.pipe(
      map(schedule => {
        if (!schedule) return null;
        
        const now = new Date();
        const startTime = new Date(schedule.startTime);
        
        // If maintenance has already started today, find next occurrence
        if (now >= startTime) {
          return this.getNextMaintenanceOccurrence(schedule);
        }
        
        // If maintenance hasn't started today, check if today is allowed
        if (this.isDayAllowedForMaintenance(schedule, now)) {
          return 'Today';
        }
        
        // Find next allowed day
        return this.getNextMaintenanceOccurrence(schedule);
      })
    );
  }

  /**
   * Get the next maintenance occurrence
   */
  private getNextMaintenanceOccurrence(schedule: MaintenanceSchedule): string {
    const now = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Check next 7 days
    for (let i = 1; i <= 7; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(now.getDate() + i);
      
      if (this.isDayAllowedForMaintenance(schedule, checkDate)) {
        return dayNames[checkDate.getDay()];
      }
    }
    
    return 'Next week';
  }

  /**
   * Get current maintenance day status
   */
  getCurrentMaintenanceDayStatus(): Observable<string | null> {
    return this.maintenanceSchedule$.pipe(
      map(schedule => {
        if (!schedule) return null;
        
        const now = new Date();
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const currentDay = dayNames[now.getDay()];
        
        if (this.isDayAllowedForMaintenance(schedule, now)) {
          return `Maintenance allowed on ${currentDay}`;
        } else {
          return `Maintenance not allowed on ${currentDay}`;
        }
      })
    );
  }

  /**
   * Test method to set a day-based maintenance schedule
   * This can be called from browser console for testing
   */
  testDayBasedMaintenance(): void {
    const now = new Date();
    const startTime = new Date(now.getTime() + 2 * 60 * 1000); // 2 minutes from now
    const endTime = new Date(now.getTime() + 5 * 60 * 1000);   // 5 minutes from now
    
    // Example: Only allow maintenance on weekends (Saturday=6, Sunday=0)
    const weekendSchedule: MaintenanceSchedule = {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      isActive: true,
      message: 'Weekend maintenance in progress',
      allowedDays: [0, 6] // Sunday and Saturday only
    };
    
    console.log('MaintenanceService: Setting weekend-only maintenance schedule');
    this.setMaintenanceSchedule(weekendSchedule);
  }

  /**
   * Cleanup when service is destroyed
   */
  ngOnDestroy(): void {
    if (this.maintenanceCheckInterval) {
      this.maintenanceCheckInterval.unsubscribe();
    }
    this.isChecking = false;
  }
} 